import * as THREE from 'three';
import { NodeMaterial } from 'three/webgpu';
import {
  Fn, uniform, vec3, vec4, float,
  smoothstep, mix, pow, step, max,
  normalWorld, positionWorld, cameraPosition, shadow
} from 'three/tsl';

// `float(1)` returns a typed scalar node; we reuse its type for the
// shadow-visibility variable so the TSL math operators chain correctly.
// ShadowNode's public TS surface is narrower than the runtime value, so the
// branches need a unifying type.
type FloatNode = ReturnType<typeof float>;

export interface ToonOptions {
  baseColor?: THREE.ColorRepresentation;
  shadowColor?: THREE.ColorRepresentation;
  midColor?: THREE.ColorRepresentation;
  highColor?: THREE.ColorRepresentation;
  rimColor?: THREE.ColorRepresentation;
  specColor?: THREE.ColorRepresentation;
  rimPower?: number;
  rimStrength?: number;
  specGloss?: number;
  specStrength?: number;
  shadowLift?: number;
}

// Colors are held as Vector3 uniforms (vec3 node type) rather than Color
// (which `uniform()` types as Node<"color">) so TSL math operators chain
// correctly through vec3 in TypeScript.
function colorToVec(c: THREE.ColorRepresentation): THREE.Vector3 {
  const col = new THREE.Color(c);
  return new THREE.Vector3(col.r, col.g, col.b);
}

// The four scalar shading knobs travel together on the GPU as a single
// vec4 uniform (rimPower / rimStrength / specGloss / specStrength). The
// shader unpacks via .x/.y/.z/.w. Setters mutate the same Vector4 in
// place — three's `.value` reference is preserved across edits, so the
// GPU sees the new component on the next draw without re-binding.
const RIM_POWER = 0;
const RIM_STRENGTH = 1;
const SPEC_GLOSS = 2;
const SPEC_STRENGTH = 3;

// Toon ramp + fresnel rim + stepped Blinn-Phong specular. Ported from the
// GLSL pair in ../shaders/toon.{vert,frag} to TSL so the material works on
// WebGPURenderer.
export class ToonMaterial extends NodeMaterial {
  readonly uBaseColor    = uniform(colorToVec('#f5d18a'));
  readonly uShadowColor  = uniform(colorToVec('#5a3b6e'));
  readonly uMidColor     = uniform(colorToVec('#c89bc4'));
  readonly uHighColor    = uniform(colorToVec('#fff2c2'));
  readonly uRimColor     = uniform(colorToVec('#ffd5a6'));
  readonly uSpecColor    = uniform(colorToVec('#fff8e0'));
  readonly uLightDir     = uniform(new THREE.Vector3(-0.4, -1.0, -0.3).normalize());
  /** Packed scalar shading knobs: x=rimPower, y=rimStrength,
   *  z=specGloss, w=specStrength. Smaller bind groups, fewer per-draw
   *  uniform slots than four loose floats. The legacy `uRimPower` etc.
   *  setters mutate components on this single Vector4. */
  readonly uShading      = uniform(new THREE.Vector4(2.6, 0.85, 24, 0.32));
  readonly uShadowLift   = uniform(0.75);
  readonly uDamageFlash  = uniform(0);

  /** Cached reference so we can rebuild colorNode if the light is swapped.
   *  When null, the shadow term is skipped entirely (HomeScreen preview path). */
  private _shadowLight: THREE.DirectionalLight | null = null;

  constructor(opts: ToonOptions = {}) {
    super();

    if (opts.baseColor    !== undefined) this.setBaseColor(opts.baseColor);
    if (opts.shadowColor  !== undefined) this.setShadowColor(opts.shadowColor);
    if (opts.midColor     !== undefined) this.setMidColor(opts.midColor);
    if (opts.highColor    !== undefined) this.setHighColor(opts.highColor);
    if (opts.rimColor     !== undefined) this.setRimColor(opts.rimColor);
    if (opts.specColor    !== undefined) this.uSpecColor.value.copy(colorToVec(opts.specColor));
    if (opts.rimPower     !== undefined) this.uShading.value.setComponent(RIM_POWER, opts.rimPower);
    if (opts.rimStrength  !== undefined) this.uShading.value.setComponent(RIM_STRENGTH, opts.rimStrength);
    if (opts.specGloss    !== undefined) this.uShading.value.setComponent(SPEC_GLOSS, opts.specGloss);
    if (opts.specStrength !== undefined) this.uShading.value.setComponent(SPEC_STRENGTH, opts.specStrength);
    if (opts.shadowLift   !== undefined) this.uShadowLift.value   = opts.shadowLift;

    this._buildColorNode();
  }

  /** Wire a directional light's cast-shadow into the toon ramp. The shadow
   *  factor attenuates `ndl` toward zero in occluded regions so shadowed
   *  surfaces flow into the existing toon "shadow" band — never pitch black,
   *  always tinted by uShadowColor / uMidColor. Triggers a one-time colorNode
   *  rebuild; subsequent renders use the cached shader. */
  setDirectionalLight(light: THREE.DirectionalLight): void {
    if (this._shadowLight === light) return;
    this._shadowLight = light;
    this._buildColorNode();
    this.needsUpdate = true;
  }

  private _buildColorNode(): void {
    const shadowLight = this._shadowLight;
    this.colorNode = Fn(() => {
      const N = normalWorld;
      const V = cameraPosition.sub(positionWorld).normalize();
      const L = this.uLightDir.negate().normalize();
      const ndlRaw = N.dot(L);

      // Cast-shadow attenuation. shadow() returns a float at runtime: 1 lit,
      // 0 occluded. Multiplying ndl by it pulls shadowed pixels toward the
      // lower toon band rather than blackening them, preserving the painted
      // look. ShadowNode's TS surface is narrower than `float()`, so we cast
      // through FloatNode (whose runtime payload is interchangeable).
      const visibility = (shadowLight
        ? (shadow(shadowLight) as unknown as FloatNode)
        : float(1)) as FloatNode;
      const ndl = ndlRaw.mul(visibility);

      // Three-band toon ramp via smoothstep edges.
      const a = smoothstep(0.05, 0.18, ndl);
      const b = smoothstep(0.42, 0.62, ndl);
      const sBand = mix(this.uShadowColor, this.uMidColor, this.uShadowLift);
      const ramp = mix(mix(sBand, this.uMidColor, a), this.uHighColor, b);
      const lit = this.uBaseColor.mul(ramp);

      // Unpack the four scalar shading knobs from the single uShading
      // vec4 (.x rimPower, .y rimStrength, .z specGloss, .w specStrength).
      // The TSL graph reads them as independent float nodes; the GPU
      // backing is a single uniform slot.
      const rimPower     = this.uShading.x;
      const rimStrength  = this.uShading.y;
      const specGloss    = this.uShading.z;
      const specStrength = this.uShading.w;

      // Stepped Blinn-Phong specular — gated by visibility so highlights
      // disappear in cast shadow.
      const H = L.add(V).normalize();
      const ndh = max(N.dot(H), 0);
      const spec = step(0.55, pow(ndh, specGloss))
        .mul(specStrength).mul(visibility);
      const withSpec = lit.add(this.uSpecColor.mul(spec));

      // Fresnel rim — independent of cast shadow (kept alive for silhouette).
      const rim = pow(max(N.dot(V), 0).oneMinus(), rimPower);
      const withRim = withSpec.add(this.uRimColor.mul(rim).mul(rimStrength));

      // Damage flash (additive warm pulse) + gamma lift.
      const flashed = withRim.add(vec3(1.0, 0.92, 0.78).mul(this.uDamageFlash));
      const out = flashed.pow(vec3(0.96));

      return vec4(out, 1);
    })();
  }

  setShadowLift(v: number): void { this.uShadowLift.value = v; }
  setDamageFlash(v: number): void { this.uDamageFlash.value = v; }
  setBaseColor(c: THREE.ColorRepresentation): void   { this.uBaseColor.value.copy(colorToVec(c)); }
  setShadowColor(c: THREE.ColorRepresentation): void { this.uShadowColor.value.copy(colorToVec(c)); }
  setMidColor(c: THREE.ColorRepresentation): void    { this.uMidColor.value.copy(colorToVec(c)); }
  setHighColor(c: THREE.ColorRepresentation): void   { this.uHighColor.value.copy(colorToVec(c)); }
  setRimColor(c: THREE.ColorRepresentation): void    { this.uRimColor.value.copy(colorToVec(c)); }
  setLightDir(v: THREE.Vector3): void {
    this.uLightDir.value.copy(v).normalize();
  }
}
